<template>
  <div class="sms-container">
    <!-- Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-icon">
          <v-icon size="28" color="#fff">mdi-message-text</v-icon>
        </div>
        <div class="header-text">
          <h1 class="header-title">Draft Bulk SMS</h1>
          <p class="header-subtitle">Send messages to multiple recipients instantly</p>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <v-card class="main-card" elevation="0">
        <!-- Recipients Section -->
        <div class="section-wrapper">
          <div class="section-header">
            <v-icon class="section-icon" color="#084468">mdi-account-multiple</v-icon>
            <h3 class="section-title">Recipients</h3>
            <v-chip 
              small 
              color="primary" 
              text-color="white" 
              v-if="toArray.length"
              class="ml-auto"
            >
              {{ toArray.length }} selected
            </v-chip>
          </div>

          <div class="recipients-container">
            <!-- Selected Recipients -->
            <div class="selected-chips" v-if="toArray.length">
              <v-chip
                v-for="chip in toArray"
                :key="chip"
                close
                color="#084468"
                text-color="white"
                class="ma-1 chip-animated"
                @click:close="onCloseChip(chip)"
              >
                <v-icon left small>mdi-account</v-icon>
                {{ formatDisplayChip(chip) }}
              </v-chip>
            </div>

            <!-- Search Input -->
            <div class="search-section">
              <v-menu 
                v-model="toSMSPopup" 
                offset-y 
                max-height="400"
                :close-on-content-click="false"
                nudge-bottom="4"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="searchKey"
                    ref="toSMS"
                    placeholder="Search contacts or type phone number..."
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    hide-details
                    class="search-input"
                    v-bind="attrs"
                    v-on="on"
                    @keyup.enter="onSelectFirstUser"
                    clearable
                  />
                </template>
                
                <!-- Dropdown Menu -->
                <v-card class="dropdown-card" elevation="8">
                  <v-list class="py-0" dense>
                    <!-- Quick Actions -->
                    <v-list-item @click="onSelectAllFans" class="quick-action-item">
                      <v-list-item-avatar>
                        <v-icon color="#084468">mdi-account-group</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-medium">
                          Select All Fanned Volunteers
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          Add all volunteers to recipients
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    
                    <v-divider v-if="filteredUsers.length"></v-divider>
                    
                    <!-- Filtered Users -->
                    <v-list-item
                      v-for="(item, index) in filteredUsers"
                      :key="index"
                      @click="selectUser(item)"
                      class="user-list-item"
                      :class="{ 'selected': toArray.includes(item.phone) }"
                    >
                      <v-list-item-avatar>
                        <v-avatar size="40" color="grey lighten-2">
                          <v-img 
                            v-if="item.midThumbnail || item.image" 
                            :src="item.midThumbnail || item.image"
                          />
                          <v-icon v-else color="grey darken-1">mdi-account</v-icon>
                        </v-avatar>
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-medium">
                          {{ item.name }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          {{ formatPhoneNumber(item.phone) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action v-if="toArray.includes(item.phone)">
                        <v-icon color="success">mdi-check-circle</v-icon>
                      </v-list-item-action>
                    </v-list-item>
                    
                    <!-- No results -->
                    <v-list-item v-if="searchKey && !filteredUsers.length" disabled>
                      <v-list-item-content>
                        <v-list-item-title class="text-center text--secondary">
                          No contacts found
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
            </div>
          </div>
        </div>

        <v-divider class="my-6"></v-divider>

        <!-- Message Section -->
        <div class="section-wrapper">
          <div class="section-header">
            <v-icon class="section-icon" color="#084468">mdi-message-text-outline</v-icon>
            <h3 class="section-title">Message Content</h3>
            <div class="ml-auto d-flex align-center">
              <v-chip 
                small 
                :color="getCharacterCountColor()" 
                text-color="white"
                class="mr-2"
              >
                {{ smsContent.length }}/160
              </v-chip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon 
                    small 
                    color="grey" 
                    v-bind="attrs" 
                    v-on="on"
                  >
                    mdi-information
                  </v-icon>
                </template>
                <span>SMS messages over 160 characters may be split into multiple messages</span>
              </v-tooltip>
            </div>
          </div>

          <div class="message-section">
            <v-textarea
              v-model="smsContent"
              placeholder="Type your message here..."
              outlined
              rows="6"
              auto-grow
              hide-details
              class="message-textarea"
              @input="onChange"
            />
            
            <!-- Message Templates -->
            <div class="templates-section mt-4">
              <v-expansion-panels flat>
                <v-expansion-panel>
                  <v-expansion-panel-header class="px-0">
                    <div class="d-flex align-center">
                      <v-icon class="mr-2" color="#084468">mdi-text-box-multiple</v-icon>
                      <span class="font-weight-medium">Quick Templates</span>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content class="px-0">
                    <div class="templates-grid">
                      <v-chip
                        v-for="template in messageTemplates"
                        :key="template.id"
                        @click="useTemplate(template.content)"
                        class="template-chip ma-1"
                        outlined
                        color="#084468"
                      >
                        {{ template.name }}
                      </v-chip>
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <v-alert
          v-if="errorMessage"
          type="error"
          dense
          outlined
          class="mt-4"
          dismissible
          @input="errorMessage = null"
        >
          {{ errorMessage }}
        </v-alert>

        <!-- Action Buttons -->
        <v-card-actions class="action-section">
          <div class="action-buttons">
            <v-btn
              v-if="$route.query._id"
              @click="onDelete"
              color="error"
              outlined
              large
              :loading="isDeleting"
              class="mr-3"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete
            </v-btn>
            
            <v-btn
              @click="onSend"
              :loading="loading"
              :disabled="!canSend"
              color="primary"
              large
              elevation="2"
              class="send-button"
            >
              <v-icon left>mdi-send</v-icon>
              {{ $route.query._id ? "Resend" : "Send Message" }}
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </div>

    <!-- Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :timeout="4000"
      color="success"
      bottom
      right
    >
      <v-icon class="mr-2">mdi-check-circle</v-icon>
      {{ snackMessage }}
    </v-snackbar>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">
          <v-icon class="mr-2" color="error">mdi-alert-circle</v-icon>
          Confirm Deletion
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this SMS campaign? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="onCloseDialog">Cancel</v-btn>
          <v-btn color="error" @click="onConfirmDelete" :loading="isDeleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "BulkSMSPage",
  data() {
    return {
      toArray: [],
      smsContent: "",
      toSMSPopup: false,
      filteredUsers: [],
      searchKey: null,
      loading: false,
      deleteDialog: false,
      isDeleting: false,
      snackbar: false,
      snackMessage: null,
      errorMessage: null,
      messageTemplates: [
        { id: 1, name: "Welcome", content: "Welcome to our community! We're excited to have you on board." },
        { id: 2, name: "Reminder", content: "Don't forget about our upcoming event. See you there!" },
        { id: 3, name: "Thank You", content: "Thank you for your participation and support. It means a lot to us!" },
        { id: 4, name: "Update", content: "We have an important update to share with you. Please check your email for details." }
      ]
    };
  },
  computed: {
    ...mapGetters("auth", {
      profile: "getProfile"
    }),
    canSend() {
      return this.toArray.length > 0 && this.smsContent.trim().length > 0;
    }
  },
  methods: {
    ...mapActions({
      sendSMS: "agency/sendAgencySMS",
      searchUsers: "chat/searchVolunteersPhone",
      getMessageLog: "messagelog/getLogDetails",
      deleteMessageLog: "messagelog/deleteLog"
    }),
    
    onChange() {
      this.errorMessage = null;
    },
    
    onSend() {
      this.errorMessage = null;
      if (this.canSend) {
        this.loading = true;
        this.sendSMS({
          toArray: this.toArray,
          content: this.smsContent
        })
          .then(message => {
            this.loading = false;
            this.toArray = [];
            this.smsContent = "";
            this.showSnackMessage(message || "Message sent successfully!");
            if (this.$route.query._id) {
              this.$router.back();
            }
          })
          .catch(error => {
            this.loading = false;
            this.errorMessage = error.response?.data?.message || "Failed to send message";
          });
      }
    },
    
    onCloseChip(chip) {
      this.toArray = this.toArray.filter(t => t !== chip);
    },
    
    onSelectFirstUser() {
      if (this.filteredUsers.length && !this.toArray.includes(this.filteredUsers[0].phone)) {
        this.toArray.push(this.filteredUsers[0].phone);
      }
      this.searchKey = "";
      this.toSMSPopup = false;
    },
    
    onSelectAllFans() {
      if (!this.toArray.includes("All Fans")) {
        this.toArray.push("All Fans");
      }
      this.searchKey = "";
      this.toSMSPopup = false;
    },
    
    selectUser(item) {
      if (!this.toArray.includes(item.phone)) {
        this.toArray.push(item.phone);
      }
      this.searchKey = "";
      this.toSMSPopup = false;
    },
    
    showSnackMessage(message) {
      this.snackbar = true;
      this.snackMessage = message;
    },
    
    formatPhoneNumber(str) {
      let cleaned = ("" + str).replace(/\D/g, "");
      let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        let intlCode = match[1] ? "+1 " : "";
        return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("");
      }
      return str;
    },
    
    formatDisplayChip(chip) {
      if (chip === "All Fans") return chip;
      return this.formatPhoneNumber(chip);
    },
    
    getCharacterCountColor() {
      const length = this.smsContent.length;
      if (length <= 120) return "success";
      if (length <= 160) return "warning";
      return "error";
    },
    
    useTemplate(content) {
      this.smsContent = content;
    },
    
    onDelete() {
      this.deleteDialog = true;
    },
    
    onCloseDialog() {
      this.deleteDialog = false;
    },
    
    onConfirmDelete() {
      this.isDeleting = true;
      this.deleteMessageLog({ _id: this.$route.query._id })
        .then(() => {
          this.$router.back();
        })
        .catch(error => {
          this.isDeleting = false;
          this.errorMessage = error.response?.data?.message || "Failed to delete";
        });
    }
  },
  
  watch: {
    searchKey(newValue) {
      if (newValue && newValue.trim()) {
        this.searchUsers(newValue.trim())
          .then(res => {
            this.filteredUsers = res || [];
          })
          .catch(error => {
            console.error("Search error:", error);
            this.filteredUsers = [];
          });
        this.toSMSPopup = true;
      } else {
        this.filteredUsers = [];
        this.toSMSPopup = false;
      }
    }
  },
  
  mounted() {
    if (this.$route.query._id) {
      this.loading = true;
      this.getMessageLog({ _id: this.$route.query._id })
        .then(res => {
          this.loading = false;
          this.toArray = res.to || [];
          this.smsContent = res.content || "";
        })
        .catch(error => {
          this.loading = false;
          this.errorMessage = error.response?.data?.message || "Failed to load message";
        });
    }
  }
};
</script>

<style scoped>
.sms-container {
  min-height: 100vh;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  padding: 20px;
  width: 100%;
}

.page-header {
  margin-bottom: 30px;
}

.header-content {
  display: flex;
  align-items: center;
  background:linear-gradient(135deg,#084468,#3b7ca1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 20px 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.header-icon {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  margin-right: 16px;
}

.header-title {
  color: white;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-subtitle {
  color: rgba(255, 255, 255, 0.8);
  margin: 4px 0 0 0;
  font-size: 14px;
}

.content-wrapper {
  max-width: 800px;
  margin: 0 auto;
}

.main-card {
  border-radius: 16px !important;
  padding: 32px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.section-wrapper {
  margin-bottom: 32px;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.section-icon {
  margin-right: 12px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.recipients-container {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
  border: 2px dashed #e9ecef;
  transition: all 0.3s ease;
}

.recipients-container:hover {
  border-color: #084468;
  background: #f5f3ff;
}

.selected-chips {
  margin-bottom: 16px;
}

.chip-animated {
  animation: chipSlideIn 0.3s ease-out;
}

@keyframes chipSlideIn {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.search-input {
  background: white;
  border-radius: 8px;
}

.dropdown-card {
  border-radius: 12px !important;
  border: 1px solid rgba(0, 0, 0, 0.1);
  max-height: 400px;
  overflow-y: auto;
}

.quick-action-item {
  background: linear-gradient(45deg, #f8f9fa, #e9ecef);
  border-bottom: 1px solid #dee2e6;
}

.user-list-item {
  transition: all 0.2s ease;
}

.user-list-item:hover {
  background: #f5f3ff !important;
}

.user-list-item.selected {
  background: #e8f5e8 !important;
}

.message-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 20px;
}

.message-textarea {
  background: white;
  border-radius: 8px;
}

.templates-section {
  background: white;
  border-radius: 8px;
  padding: 16px;
}

.templates-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.template-chip {
  transition: all 0.2s ease;
  cursor: pointer;
}

.template-chip:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.action-section {
  padding: 24px 0 0 0 !important;
  border-top: 1px solid #e9ecef;
  margin-top: 24px;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.send-button {
  min-width: 180px;
  font-weight: 600;
  text-transform: none;
  font-size: 16px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .sms-container {
    padding: 12px;
  }
  
  .main-card {
    padding: 20px;
  }
  
  .header-content {
    padding: 16px;
  }
  
  .header-title {
    font-size: 20px;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 12px;
  }
  
  .send-button {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .header-icon {
    margin-right: 0;
    margin-bottom: 12px;
  }
  
  .section-header {
    flex-wrap: wrap;
    gap: 8px;
  }
  
  .templates-grid {
    flex-direction: column;
  }
  
  .template-chip {
    width: 100%;
    justify-content: center;
  }
}

/* Custom scrollbar */
.dropdown-card::-webkit-scrollbar {
  width: 6px;
}

.dropdown-card::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.dropdown-card::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.dropdown-card::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>